import { Input, InputV2 } from "@southfields-digital/mxmz-react-ui";

import { DataControllerInput } from "src/components/DataController";
import {
	extractImageUrlFromSuggestion,
	extractValueFromSuggestion,
	hasImageSuggestion,
} from "src/components/DataController/utils";
import { getRepeaterItems, getValues } from "src/components/GraphicPropertiesForm/utils";
import TimerInput from "src/components/InputTimer";
import Repeater, { type Id, type Items } from "src/components/Repeater";
import { toDatetimeLocal } from "src/utils";

type PropertyFieldProps = {
	field: WithValue;
	onChange: (value: string | number | PropertyValue[]) => void;
	onSelectMedia?: (
		properties: ImageProperties,
		handleSelectValue?: (value: string) => void,
	) => void;
};

export default function PropertyField({
	field,
	onChange,
	onSelectMedia,
}: PropertyFieldProps): React.ReactNode {
	const { id, name, type, value } = field;

	switch (type) {
		case "text":
			return (
				<DataControllerInput
					value={typeof value === "string" ? value : ""}
					onChange={(e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
						onChange(e.currentTarget.value)
					}
					multiline={field.multiline}
					maxLength={field.maxLength}
					dataSources={["matchControl"]}
					placeholder={field.placeholder || name}
					valueExtractor={extractValueFromSuggestion}
				/>
			);
		case "url":
			return (
				<InputV2
					type="url"
					value={typeof value === "string" ? value : ""}
					placeholder={field.placeholder}
					onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
				/>
			);
		case "color":
			return (
				<InputV2
					type="colorPicker"
					value={typeof value === "string" ? value : ""}
					onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
				/>
			);
		case "number":
			return (
				<InputV2
					type="number"
					value={
						typeof field.value === "string" || typeof field.value === "number"
							? String(field.value)
							: ""
					}
					onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
				/>
			);
		case "percentage":
			return (
				<InputV2
					type="number"
					min={0}
					max={100}
					suffix="%"
					value={typeof value === "string" || typeof value === "number" ? value : ""}
					onChange={(e: React.SyntheticEvent<HTMLInputElement>) => onChange(e.currentTarget.value)}
				/>
			);
		case "select":
			return (
				<InputV2
					type="select"
					value={typeof value === "string" ? value : ""}
					options={field.options}
					onChange={(e: React.SyntheticEvent<HTMLSelectElement>) => onChange(e.currentTarget.value)}
				/>
			);
		case "image":
			return (
				<DataControllerInput
					variant="selectableInput"
					value={typeof value === "string" ? value : ""}
					onChange={(e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
						onChange(e.currentTarget.value)
					}
					dataSources={["matchControl"]}
					// The value is a URL, so it is no use as a search term: show every
					// suggestion that carries an image on focus, and let the media library
					// handle the rest.
					valueExtractor={extractImageUrlFromSuggestion}
					suggestionFilter={hasImageSuggestion}
					showOnFocus
					filterByValue={false}
					onRemoveMedia={() => onChange("")}
					onClickMedia={() => onSelectMedia?.(field, (v) => onChange(v))}
				/>
			);
		case "repeater":
			return (
				<Repeater
					id={id}
					items={getRepeaterItems(field, Array.isArray(value) ? value : undefined)}
					onChangeRows={(_id: Id, _rowIndex: number, rows: Items[]) => onChange(getValues(rows))}
					handleSelectMedia={onSelectMedia}
				/>
			);
		case "countdown": {
			const inputValue =
				typeof value === "string" && value ? toDatetimeLocal(new Date(value)).localDateTime : "";
			return (
				<Input
					type="datetime-local"
					id={id}
					name={name}
					label={name}
					value={inputValue}
					onChange={(e: React.SyntheticEvent<HTMLInputElement>) => {
						const v = e.currentTarget.value;
						onChange(
							typeof v === "string" && !isNaN(Date.parse(v)) ? new Date(v).toISOString() : "",
						);
					}}
				/>
			);
		}
		case "timer":
			return (
				<TimerInput
					id={id}
					name={name}
					value={typeof value === "number" ? value : 0}
					onChange={(v: number) => onChange(v)}
				/>
			);
		default:
			return null;
	}
}
